import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { RenderModule } from 'holocron';
import { getValueFromPropsOrRouteProps, loadModuleData } from '../utils';

const getContainerClassName = ({ containerClassName, route }) => [containerClassName, route.containerClassName, 'container pad-1-tb'].find(
  (value) => value !== undefined
);

const AxpCommercialReconciliationWrapper = ({
  children,
  isHeaderDisabled,
  isFooterDisabled,
  headerModule,
  footerModule,
  HeaderComponent,
  FooterComponent,
  sanModule,
  isHeaderLoaded,
  isFooterLoaded,
  isSanLoaded,
  ...props
}) => {
  const containerClass = getContainerClassName(props);

  const hProps = getValueFromPropsOrRouteProps('headerProps', props, {});
  const sanProps = getValueFromPropsOrRouteProps('siteAreaNavProps', props, {});
  const fProps = getValueFromPropsOrRouteProps('footerProps', props, {});
  const headerProps = props.headerComponent ? hProps : { props: hProps };
  const footerProps = props.footerComponent ? fProps : { props: fProps };

  const header = !isHeaderDisabled && isHeaderLoaded && (
    <HeaderComponent moduleName={headerModule} {...headerProps} />
  );

  const siteAreaNavModule = sanModule && isSanLoaded && (
    <RenderModule moduleName={sanModule} props={sanProps} />
  );

  const footer = !isFooterDisabled && isFooterLoaded && (
    <FooterComponent moduleName={footerModule} {...footerProps} />
  );

  return (
    <div data-module-name="axp-commercial-reconciliation-wrapper" className="body">
      {header}
      <div>{siteAreaNavModule}</div>
      <div className={containerClass}>
        {children}
      </div>
      {footer}
    </div>
  );
};

AxpCommercialReconciliationWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  isHeaderDisabled: PropTypes.bool,
  isFooterDisabled: PropTypes.bool,
  isHeaderLoaded: PropTypes.bool,
  isFooterLoaded: PropTypes.bool,
  headerComponent: PropTypes.node,
  footerComponent: PropTypes.node,
  headerProps: PropTypes.shape({}),
  footerProps: PropTypes.shape({}),
  containerClassName: PropTypes.string,
  siteAreaNavModuleName: PropTypes.string,
  header: PropTypes.string,
  footer: PropTypes.string,
  route: PropTypes.shape({
    containerClassName: PropTypes.string,
    siteAreaNavModuleName: PropTypes.string,
    header: PropTypes.string,
    footer: PropTypes.string,
  }),
  headerModule: PropTypes.string.isRequired,
  footerModule: PropTypes.string.isRequired,
  sanModule: PropTypes.string,
  isSanLoaded: PropTypes.bool.isRequired,
};

AxpCommercialReconciliationWrapper.defaultProps = {
  isHeaderDisabled: false,
  isFooterDisabled: false,
  containerClassName: undefined,
  siteAreaNavModuleName: '',
  headerProps: null,
  footerProps: null,
  header: '',
  footer: '',
  route: {
    header: '',
    footer: '',
    isHeaderDisabled: false,
    isFooterDisabled: false,
    containerClassName: undefined,
    siteAreaNavModuleName: '',
  },
};

export const shouldModuleReload = (currentProps, nextProps) => getValueFromPropsOrRouteProps('siteAreaNavModuleName', currentProps, '')
  !== getValueFromPropsOrRouteProps('siteAreaNavModuleName', nextProps, '');

AxpCommercialReconciliationWrapper.holocron = {
  name: 'axp-commercial-reconciliation-wrapper',
  loadModuleData,
  shouldModuleReload,
};

const getIsModuleLoaded = (state, module) => state.hasIn(['holocron', 'loaded', module]);

export const mapStateToProps = (state, props) => {
  const HeaderComponent = getValueFromPropsOrRouteProps('headerComponent', props, RenderModule);
  const headerModule = getValueFromPropsOrRouteProps('header', props, 'axp-global-header');
  const isHeaderDisabled = getValueFromPropsOrRouteProps('isHeaderDisabled', props, false);
  const isHeaderLoaded = getIsModuleLoaded(state, headerModule);

  const FooterComponent = getValueFromPropsOrRouteProps('footerComponent', props, RenderModule);
  const footerModule = getValueFromPropsOrRouteProps('footer', props, 'axp-footer');
  const isFooterDisabled = getValueFromPropsOrRouteProps('isFooterDisabled', props, false);
  const isFooterLoaded = getIsModuleLoaded(state, footerModule);

  const sanModule = getValueFromPropsOrRouteProps('siteAreaNavModuleName', props, '');
  const isSanLoaded = Boolean(sanModule) && getIsModuleLoaded(state, sanModule);

  return {
    HeaderComponent,
    headerModule,
    isHeaderLoaded,
    isHeaderDisabled,
    FooterComponent,
    footerModule,
    isFooterLoaded,
    isFooterDisabled,
    sanModule,
    isSanLoaded,
  };
};

export const TestableAxpCommercialReconciliationWrapper = AxpCommercialReconciliationWrapper;
export default connect(mapStateToProps)(AxpCommercialReconciliationWrapper);
